import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface ScrollPosition {
  x: number;
  y: number;
  timestamp: number;
}

const SCROLL_POSITIONS_KEY = 'recipe-app-scroll-positions';
const POSITION_EXPIRY = 30 * 60 * 1000; // 30 minutes

class ScrollPositionManager {
  private positions: Map<string, ScrollPosition> = new Map();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(SCROLL_POSITIONS_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        const now = Date.now();
        
        // Filter out expired positions
        Object.entries(data).forEach(([key, position]: [string, any]) => {
          if (now - position.timestamp < POSITION_EXPIRY) {
            this.positions.set(key, position);
          }
        });
      }
    } catch {
    }
  }

  private saveToStorage() {
    try {
      const data = Object.fromEntries(this.positions);
      sessionStorage.setItem(SCROLL_POSITIONS_KEY, JSON.stringify(data));
    } catch {
    }
  }

  savePosition(key: string, x: number = window.scrollX, y: number = window.scrollY) {
    this.positions.set(key, {
      x,
      y,
      timestamp: Date.now()
    });
    this.saveToStorage();
  }

  getPosition(key: string): ScrollPosition | null {
    return this.positions.get(key) || null;
  }

  restorePosition(key: string): boolean {
    const position = this.getPosition(key);
    if (position) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        window.scrollTo({
          left: position.x,
          top: position.y,
          behavior: 'auto' // Instant scroll for better UX
        });
      });
      return true;
    }
    return false;
  }

  clearPosition(key: string) {
    this.positions.delete(key);
    this.saveToStorage();
  }

  clearExpiredPositions() {
    const now = Date.now();
    let changed = false;
    
    this.positions.forEach((position, key) => {
      if (now - position.timestamp >= POSITION_EXPIRY) {
        this.positions.delete(key);
        changed = true;
      }
    });
    
    if (changed) {
      this.saveToStorage();
    }
  }
}

// Singleton instance
const scrollManager = new ScrollPositionManager();

export const useScrollPosition = () => {
  const location = useLocation();

  // Clean up expired positions periodically
  useEffect(() => {
    scrollManager.clearExpiredPositions();
  }, [location.pathname]);

  const saveScrollPosition = useCallback((key?: string) => {
    const positionKey = key || location.pathname;
    scrollManager.savePosition(positionKey);
  }, [location.pathname]);

  const restoreScrollPosition = useCallback((key?: string) => {
    const positionKey = key || location.pathname;
    return scrollManager.restorePosition(positionKey);
  }, [location.pathname]);

  const clearScrollPosition = useCallback((key?: string) => {
    const positionKey = key || location.pathname;
    scrollManager.clearPosition(positionKey);
  }, [location.pathname]);

  return {
    saveScrollPosition,
    restoreScrollPosition,
    clearScrollPosition
  };
};